import { ReactNode, useState } from "react";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { QuickCaptureModal } from "../quick-capture/QuickCaptureModal";
import { MobileBottomNav } from "./MobileBottomNav";
import { PreferencesProvider } from "@/contexts/PreferencesContext";
import { NavigationProvider, useNavigation } from "@/contexts/NavigationContext";
import { PageTitleProvider } from "@/contexts/PageTitleContext";
import { NavigationLoader } from "@/components/ui/navigation-loader";
import { useGlobalRealtime } from "@/hooks/useGlobalRealtime";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children: ReactNode;
}

const AppLayoutContent = ({ children }: AppLayoutProps) => {
  const [isQuickCaptureOpen, setIsQuickCaptureOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const { isNavigating } = useNavigation();
  
  // Global realtime subscription - runs once for entire app
  useGlobalRealtime();

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      <Sidebar 
        onQuickCapture={() => setIsQuickCaptureOpen(true)}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
      />
      
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar onMobileMenuToggle={() => setIsMobileSidebarOpen(true)} />
        
        <main className={cn(
          "relative flex-1 overflow-auto p-3 sm:p-4 lg:p-6",
          isMobile && "pb-24",
          isNavigating && "overflow-hidden" // Prevent scroll during loading
        )}>
          <NavigationLoader isMobile={isMobile} />
          {children}
        </main>
      </div>

      <QuickCaptureModal 
        isOpen={isQuickCaptureOpen} 
        onClose={() => setIsQuickCaptureOpen(false)} 
      />

      {isMobile && (
        <MobileBottomNav onQuickCapture={() => setIsQuickCaptureOpen(true)} />
      )}
    </div>
  );
};

export const AppLayout = ({ children }: AppLayoutProps) => {
  return (
    <NavigationProvider>
      <PreferencesProvider>
        <PageTitleProvider>
          <AppLayoutContent>{children}</AppLayoutContent>
        </PageTitleProvider>
      </PreferencesProvider>
    </NavigationProvider>
  );
};
